import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronRight as DrillIcon, CornerUpLeft, ExternalLink } from 'lucide-react';
import { apiGet } from '../api/client.js';
import Spinner from './Spinner.jsx';

const fmt    = (n) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(parseFloat(n) || 0);
const fmtNum = (n) => new Intl.NumberFormat('es-AR').format(n || 0);

const todayStr   = () => new Date().toISOString().slice(0, 10);
const offsetDate = (d, n) => {
  const dt = new Date(d + 'T12:00:00');
  dt.setDate(dt.getDate() + n);
  return dt.toISOString().slice(0, 10);
};
const fmtDate = (d) =>
  new Date(d + 'T12:00:00').toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'short' });

const PM_LABEL = { credit_card: 'Crédito', debit_card: 'Débito', qr: 'QR', sin_medio: 'Sin medio' };
const LEVEL_LABEL = { store: 'Por sucursal', pos: 'Por terminal / POS', payment: 'Por medio de cobro' };

export default function DayDrillCard({ clientId, isSuperAdmin }) {
  const navigate = useNavigate();
  const [date,      setDate]      = useState(todayStr);
  const [storeId,   setStoreId]   = useState(null);
  const [storeName, setStoreName] = useState('');
  const [posId,     setPosId]     = useState(null);
  const [posName,   setPosName]   = useState('');

  const resolvedClientId = isSuperAdmin ? clientId : null;
  const level = posId ? 'payment' : storeId ? 'pos' : 'store';

  const q = new URLSearchParams();
  if (resolvedClientId) q.set('clientId', resolvedClientId);
  q.set('date', date);
  if (storeId) q.set('storeId', storeId);
  if (posId)   q.set('posId', posId);

  const { data, isLoading } = useQuery({
    queryKey: ['day-drill', resolvedClientId, date, storeId, posId],
    queryFn: () => apiGet(`/orders/day-drill?${q.toString()}`),
    staleTime: 60_000,
  });

  const rows  = data?.rows || [];
  const total = rows.reduce((a, r) => ({ count: a.count + r.count, amount: a.amount + parseFloat(r.amount) }), { count: 0, amount: 0 });
  const isToday = date === todayStr();

  const drillInto = (row) => {
    if (level === 'store' && row.key !== 'sin_suc') { setStoreId(row.key); setStoreName(row.name); }
    if (level === 'pos'   && row.key !== 'sin_pos') { setPosId(row.key);   setPosName(row.name);   }
  };

  const goBack = () => {
    if (posId) { setPosId(null); setPosName(''); }
    else       { setStoreId(null); setStoreName(''); }
  };

  // Navegar a órdenes con filtros del contexto actual
  const goToOrders = (row) => {
    const initFilters = { dateFrom: date, dateTo: date };
    if (level === 'store' && row && row.key !== 'sin_suc') initFilters.storeId = row.key;
    if (level === 'pos'   && storeId) initFilters.storeId = storeId;
    if (level === 'payment') {
      if (storeId) initFilters.storeId = storeId;
      if (row && row.key !== 'sin_medio') initFilters.paymentMethod = row.key;
    }
    navigate('/orders', { state: { initFilters } });
  };

  const goToOrdersTotal = () => {
    const initFilters = { dateFrom: date, dateTo: date };
    if (storeId) initFilters.storeId = storeId;
    navigate('/orders', { state: { initFilters } });
  };

  const canDrill = level !== 'payment';

  return (
    <div className="card space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          {level !== 'store' && (
            <button onClick={goBack}
              className="flex items-center gap-1 text-xs rounded-lg px-2 py-1 transition-colors hover:bg-accent/10"
              style={{ color: 'var(--text-muted)' }}>
              <CornerUpLeft size={12} />
              {level === 'pos' ? 'Sucursales' : storeName}
            </button>
          )}
          <span className="text-xs font-semibold truncate"
            style={{ color: level === 'store' ? 'var(--text-muted)' : 'var(--text-secondary)' }}>
            {level === 'store' ? 'RESUMEN DEL DÍA' : level === 'pos' ? storeName : posName}
          </span>
        </div>

        {/* Day navigation */}
        <div className="flex items-center gap-0.5 shrink-0">
          <button onClick={() => setDate(d => offsetDate(d, -1))}
            className="w-6 h-6 flex items-center justify-center rounded-md transition-colors hover:bg-accent/10"
            style={{ color: 'var(--text-muted)' }}>
            <ChevronLeft size={14} />
          </button>
          <span className="text-xs font-medium px-1 whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>
            {isToday ? 'Hoy' : fmtDate(date)}
          </span>
          <button onClick={() => setDate(d => offsetDate(d, 1))} disabled={isToday}
            className="w-6 h-6 flex items-center justify-center rounded-md transition-colors hover:bg-accent/10 disabled:opacity-30"
            style={{ color: 'var(--text-muted)' }}>
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{LEVEL_LABEL[level]}</p>

      {isLoading ? (
        <div className="h-24 flex items-center justify-center"><Spinner /></div>
      ) : !rows.length ? (
        <p className="text-sm py-6 text-center" style={{ color: 'var(--text-muted)' }}>
          Sin órdenes {isToday ? 'hoy' : `el ${fmtDate(date)}`}
        </p>
      ) : (
        <div className="space-y-0.5">
          {rows.map((row) => {
            const isDrillable = canDrill && row.key !== 'sin_suc' && row.key !== 'sin_pos';
            const pct  = total.amount > 0 ? parseFloat(row.amount) / total.amount : 0;
            const name = PM_LABEL[row.key] ?? row.name;
            return (
              <div key={row.key}
                className="flex items-center gap-2 rounded-lg px-2 py-1.5 group"
                style={{ cursor: isDrillable ? 'pointer' : 'default' }}
                onClick={() => isDrillable && drillInto(row)}
                onMouseEnter={e => isDrillable && (e.currentTarget.style.background = 'var(--bg-elevated)')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}>

                {/* Nombre con barra proporcional */}
                <div className="relative flex-1 min-w-0">
                  <div className="absolute inset-y-0 left-0 rounded-sm opacity-10 pointer-events-none"
                    style={{ width: `${(pct * 100).toFixed(1)}%`, background: 'var(--accent)' }} />
                  <span className="relative text-sm font-medium truncate block" style={{ color: 'var(--text)' }}>
                    {name}
                  </span>
                </div>

                {/* Cantidad — clicable */}
                <button
                  onClick={e => { e.stopPropagation(); goToOrders(row); }}
                  className="w-16 text-right text-xs tabular-nums rounded px-1 py-0.5 transition-colors hover:bg-accent/15 hover:text-accent shrink-0 flex items-center justify-end gap-0.5"
                  style={{ color: 'var(--text-muted)' }}
                  title="Ver órdenes">
                  {fmtNum(row.count)}
                  <ExternalLink size={9} className="opacity-0 group-hover:opacity-60 transition-opacity" />
                </button>

                {/* Monto */}
                <span className="w-24 text-right text-sm font-semibold tabular-nums shrink-0" style={{ color: 'var(--text)' }}>
                  {fmt(row.amount)}
                </span>

                {/* Chevron drill */}
                <div className="w-3.5 shrink-0 flex items-center justify-center">
                  {isDrillable && (
                    <DrillIcon size={12} className="opacity-0 group-hover:opacity-40 transition-opacity"
                      style={{ color: 'var(--text-muted)' }} />
                  )}
                </div>
              </div>
            );
          })}

          {/* Total */}
          <div className="flex items-center gap-2 px-2 pt-2 mt-1 border-t" style={{ borderColor: 'var(--border)' }}>
            <span className="flex-1 text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>TOTAL</span>

            <button
              onClick={goToOrdersTotal}
              className="w-16 text-right text-xs tabular-nums rounded px-1 py-0.5 transition-colors hover:bg-accent/15 hover:text-accent shrink-0 flex items-center justify-end gap-0.5"
              style={{ color: 'var(--text-muted)' }}
              title="Ver todas las órdenes del día">
              {fmtNum(total.count)}
              <ExternalLink size={9} />
            </button>

            <span className="w-24 text-right text-sm font-bold tabular-nums shrink-0" style={{ color: 'var(--accent)' }}>
              {fmt(total.amount)}
            </span>

            <div className="w-3.5 shrink-0" />
          </div>
        </div>
      )}
    </div>
  );
}
